import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  CalendarSchedulesResponse,
  CreateWorkSchedulePayload,
  EmptyResponse,
  EmployeesWorkSchedulesResponse,
  LocationReference,
  Response,
  UpdateWorkSchedulePayload,
  WorkScheduleResponse,
} from "~/types/api";

export function createWorkScheduleApi({ getRequest, postRequest, putRequest, deleteRequest }: ApiRequestHelpers) {
  /** Fetches work schedules grouped by day with total hours. */
  const getWorkSchedules = (date?: string) =>
    getRequest<Response<Record<string, { schedules: WorkScheduleResponse[]; hours: number }>>>(
      `/schedules${date ? `?date=${date}` : ""}`,
    );

  /** Fetches work schedules for specific employees. */
  const getEmployeesWorkSchedules = (employeeIds: number[], date?: string) => {
    const params = new URLSearchParams();
    employeeIds.forEach((id) => params.append("employee_ids[]", String(id)));
    if (date) params.append("date", date);
    return getRequest<Response<EmployeesWorkSchedulesResponse>>(`/schedules/employees?${params.toString()}`);
  };

  /** Fetches calendar-view schedules for the given users. */
  const getCalendarSchedules = (userIds: number[], date?: string, locationId?: number) => {
    const params = new URLSearchParams();
    userIds.forEach((id) => params.append("user_ids[]", String(id)));
    if (date) params.append("date", date);
    if (locationId) params.append("location_id", String(locationId));
    return getRequest<Response<CalendarSchedulesResponse>>(`/schedules/calendar?${params.toString()}`);
  };

  /** Fetches the locations an employee is assigned to. */
  const getEmployeeLocations = (userId: number) =>
    getRequest<{ data: LocationReference[] }>(`/schedules/employees/${userId}/locations`);

  /** Creates a new work schedule entry. */
  const createWorkSchedule = (payload: CreateWorkSchedulePayload) =>
    postRequest<Response<WorkScheduleResponse>>("/schedules", payload);

  /** Updates an existing work schedule. */
  const updateWorkSchedule = (id: string, payload: UpdateWorkSchedulePayload) =>
    putRequest<Response<WorkScheduleResponse>>(`/schedules/${id}`, payload);

  /** Deletes a work schedule or a single occurrence. */
  const deleteWorkSchedule = (id: string, body: { delete_type: string; occurrence_date: string }) =>
    deleteRequest<EmptyResponse>(`/schedules/${id}`, { data: body });

  return {
    getWorkSchedules,
    getEmployeesWorkSchedules,
    getCalendarSchedules,
    getEmployeeLocations,
    createWorkSchedule,
    updateWorkSchedule,
    deleteWorkSchedule,
  };
}

export type WorkScheduleApi = ReturnType<typeof createWorkScheduleApi>;
